import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiPodService, type AiPod } from '../../services/aiPodService';
import {
  aiPodBillingService,
  formatMoney,
  isLiveStatus,
  isUsableStatus,
  usedUpLimits,
  planPriceForCycle,
  podOpenPath,
  podStatusLabel,
  type AiPodBillingPlansResponse,
  type PodSubscription,
} from '../../services/aiPodBillingService';
import type { BillingPlanDto } from '../../services/billingService';
import { Badge } from '../ui/Badge';
import { Button, Spinner } from '../ui/Button';
import { Surface } from '../ui/Surface';
import { useThemeColor } from '../../context/BrandingContext';

// Derive a stable background color for a pod icon from its name
const ICON_COLORS = [
  { bg: '#E8F0FE', text: '#1A56DB' },
  { bg: '#FCE8FF', text: '#9F1AB1' },
  { bg: '#FEF3C7', text: '#92400E' },
  { bg: '#D1FAE5', text: '#065F46' },
  { bg: '#FEE2E2', text: '#991B1B' },
  { bg: '#E0E7FF', text: '#3730A3' },
];
function podIconColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return ICON_COLORS[h % ICON_COLORS.length];
}

// Derive avatar initials and color from an agent type name
const AVATAR_COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6366F1'];
function avatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

// Mode tags from metadata; without them, describe what the pod provisions.
function podModeTags(pod: AiPod): string[] {
  const m = (pod.metadata as any)?.modes;
  if (Array.isArray(m) && m.length) return m;
  return pod.channel === 'chat' ? ['Chats'] : pod.channel === 'both' ? ['Chats', 'Automated'] : ['Automated'];
}

// Get channel tags from metadata
function podChannels(pod: AiPod): string[] {
  const c = (pod.metadata as any)?.channels;
  if (Array.isArray(c)) return c;
  return [];
}

// Get badge label from metadata (e.g. "Most popular")
function podBadge(pod: AiPod): string | null {
  return (pod.metadata as any)?.badge ?? null;
}

/** Cheapest monthly-equivalent price across a pod's plans, or null when it has none. */
function cheapest(plans: BillingPlanDto[]): { amountMinor: number; currency: string } | null {
  const prices = plans
    .map((p) => {
      const monthly = planPriceForCycle(p, 'monthly');
      if (monthly) return { amountMinor: monthly.amountMinor, currency: monthly.currency };
      const yearly = planPriceForCycle(p, 'yearly');
      return yearly ? { amountMinor: Math.round(yearly.amountMinor / 12), currency: yearly.currency } : null;
    })
    .filter((x): x is { amountMinor: number; currency: string } => !!x);
  return prices.length ? prices.reduce((a, b) => (b.amountMinor < a.amountMinor ? b : a)) : null;
}

const ALL_PODS = 'All Pods';

type PodPlanCache = Record<string, BillingPlanDto[]>;

export const AiPodMarketplaceGrid: React.FC = () => {
  // Avatar colours: the blue and purple ones follow the client's theme.
  const themeColor = useThemeColor();
  const navigate = useNavigate();
  const [pods, setPods] = useState<AiPod[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [subscriptions, setSubscriptions] = useState<Record<string, PodSubscription>>({});
  const [planCache, setPlanCache] = useState<PodPlanCache>({});

  const [activeCat, setActiveCat] = useState(ALL_PODS);
  const [activeModes, setActiveModes] = useState<string[]>([]);

  const refreshList = async () => {
    setLoading(true);
    try {
      const data = await aiPodService.listAiPods({ isActive: true, search: searchQuery || '' });
      setPods(data.aiPods || []);
    } catch (e) {
      console.error('Failed to load AI pods:', e);
    } finally {
      setLoading(false);
    }
  };

  const refreshSubscriptions = async () => {
    try {
      const list = await aiPodBillingService.listSubscriptions();
      setSubscriptions(Object.fromEntries(list.map((s) => [s.aiPodId, s])));
    } catch {
      setSubscriptions({});
    }
  };

  // Pre-fetch plans for all pods to show "From $X/mo" pricing
  const fetchPlansForPods = async (podList: AiPod[]) => {
    const results: PodPlanCache = {};
    await Promise.all(
      podList.map(async (pod) => {
        try {
          const res: AiPodBillingPlansResponse = await aiPodBillingService.listBillingPlans(pod.id);
          results[pod.id] = res.plans || [];
        } catch {
          results[pod.id] = [];
        }
      })
    );
    setPlanCache((prev) => ({ ...prev, ...results }));
  };

  useEffect(() => {
    const t = setTimeout(() => refreshList(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (pods.length) fetchPlansForPods(pods);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pods]);

  useEffect(() => {
    void refreshSubscriptions();
  }, []);

  // Goal filters come from the categories the listed pods actually have.
  const categories = useMemo(() => {
    const seen = new Set<string>();
    for (const pod of pods) {
      if (pod.category?.name) seen.add(pod.category.name);
    }
    return [ALL_PODS, ...[...seen].sort()];
  }, [pods]);

  const filteredPods = useMemo(() => {
    return pods.filter((pod) => {
      if (activeCat !== ALL_PODS) {
        const cat = pod.category?.name ?? '';
        if (cat !== activeCat) return false;
      }
      if (activeModes.length) {
        const modes = podModeTags(pod);
        if (!activeModes.some((m) => modes.includes(m))) return false;
      }
      return true;
    });
  }, [pods, activeCat, activeModes]);

  const toggleMode = (mode: string) => {
    setActiveModes((prev) =>
      prev.includes(mode) ? prev.filter((m) => m !== mode) : [...prev, mode]
    );
  };

  // SVG icons for mode tags — matches reference icons
  const ModeIcon = ({ mode }: { mode: string }) => {
    if (mode === 'Automated') return (
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    );
    if (mode === 'Chats') return (
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    );
    if (mode === 'Talks') return (
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    );
    return null;
  };

  return (
    <>
      {/* Filter bar — Goal tabs left, How it works + mode tags right */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-agentic-text-secondary font-medium">Goal</span>
          {categories.map((cat) => (
            <Button tone="custom"
              key={cat}
              onClick={() => setActiveCat(cat)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                activeCat === cat
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-agentic-text-primary border border-agentic-gray-200 hover:border-gray-400'
              }`}
            >
              {cat}
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-agentic-text-secondary font-medium">How it works</span>
          {(['Talks', 'Chats', 'Automated'] as const).map((mode) => (
            <Button tone="custom"
              key={mode}
              onClick={() => toggleMode(mode)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                activeModes.includes(mode)
                  ? 'border-agentic-primary-blue bg-agentic-primary-blue/10 text-agentic-primary-blue'
                  : 'border-agentic-gray-200 bg-white text-agentic-text-primary hover:border-gray-400'
              }`}
            >
              <ModeIcon mode={mode} />
              {mode}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-sm text-agentic-text-secondary gap-2">
          <Spinner className="h-4 w-4 animate-spin rounded-full border-2 border-agentic-primary-blue border-t-transparent" />
          Loading pods…
        </div>
      ) : filteredPods.length === 0 ? (
        <div className="py-20 text-center">
          <div className="mb-3 text-5xl">📦</div>
          <h3 className="mb-1.5 text-lg font-semibold text-agentic-text-primary">No pods found</h3>
          <p className="text-sm text-agentic-text-secondary">Try a different filter or check back soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredPods.map((pod) => {
            const sub = subscriptions[pod.id];
            const live = sub && isLiveStatus(sub.status) ? sub : null;
            const openPath = live && isUsableStatus(live.status) ? podOpenPath(live) : null;
            // Fully used for this period: the tenant still has the pod, it's just out of allowance.
            const limitReached = !!live && isUsableStatus(live.status) && usedUpLimits(live).length > 0;
            const orderedAgentTypes = [...(pod.agentTypes ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);
            const badge = podBadge(pod);
            const channels = podChannels(pod);
            const modes = podModeTags(pod);
            const plans = planCache[pod.id] ?? [];
            const minPrice = cheapest(plans);

            return (
              <Surface
                key={pod.id}
                className="relative flex flex-col rounded-2xl bg-white border border-agentic-gray-200 p-5 hover:shadow-md transition-shadow duration-200"
              >
                {/* Top row: icon + Most popular badge */}
                <div className="flex items-start justify-between mb-3">
                  {/* Purple icon square */}
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-c-5c4fcf text-white text-2xl font-bold">
                    {pod.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {badge && (
                      <Badge className="rounded-full bg-orange-50 border border-orange-200 px-2.5 py-0.5 text-[11px] font-semibold text-orange-600">
                        {badge}
                      </Badge>
                    )}
                    {live && (
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          isUsableStatus(live.status) && !live.cancelAtPeriodEnd
                            ? 'bg-sky-50 text-sky-700 border-sky-200'
                            : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}
                      >
                        {podStatusLabel(live)}
                      </span>
                    )}
                    {limitReached && (
                      <span
                        className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
                        title="This plan's allowance is fully used for the current period"
                      >
                        Plan fully used
                      </span>
                    )}
                  </div>
                </div>

                {/* Name */}
                <h3 className="text-lg font-bold text-agentic-text-primary leading-snug">{pod.name}</h3>

                {/* Mode tags — pill with border and icon */}
                {modes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {modes.map((m) => (
                      <Badge key={m} className="inline-flex items-center gap-1 rounded-full border border-agentic-gray-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-agentic-text-secondary">
                        <ModeIcon mode={m} />
                        {m}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Description */}
                <p className="mt-2.5 text-sm text-agentic-text-secondary leading-relaxed line-clamp-3">
                  {pod.shortDescription || pod.description || 'A ready-to-deploy AI team for your business.'}
                </p>

                {/* Channel chips — purple tinted */}
                {channels.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {channels.map((ch) => (
                      <Badge key={ch} className="rounded-full bg-c-ede9ff px-2.5 py-0.5 text-[11px] font-medium text-c-5c4fcf">
                        {ch}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Agent avatars */}
                {orderedAgentTypes.length > 0 && (
                  <div className="mt-3 flex items-center gap-2">
                    <div className="flex -space-x-2">
                      {orderedAgentTypes.slice(0, 4).map((ref) => (
                        <div
                          key={ref.id}
                          title={ref.agentType.name}
                          className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-[9px] font-bold text-white"
                          style={{ background: themeColor(avatarColor(ref.agentType.name)) }}
                        >
                          {initials(ref.agentType.name)}
                        </div>
                      ))}
                      {orderedAgentTypes.length > 4 && (
                        <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-agentic-gray-100 text-[9px] font-medium text-agentic-text-secondary">
                          +{orderedAgentTypes.length - 4}
                        </div>
                      )}
                    </div>
                    <span className="text-[11px] text-agentic-text-secondary">
                      {orderedAgentTypes.length} agent{orderedAgentTypes.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}

                {/* Footer: price + CTA */}
                <div className="mt-auto pt-4 flex items-center justify-between gap-2">
                  {live ? (
                    <>
                      <Button tone="custom"
                        onClick={() => navigate(`/pods/${pod.id}/manage`)}
                        className="rounded-xl border border-agentic-gray-300 bg-white px-3 py-2 text-xs font-medium text-agentic-text-secondary hover:border-gray-400 transition-all"
                        title="Plan, usage, billing and cancellation"
                      >
                        Manage
                      </Button>
                      {openPath ? (
                        <Button tone="custom"
                          onClick={() => navigate(openPath)}
                          className="rounded-xl bg-emerald-500 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-600 transition-colors"
                        >
                          Open
                        </Button>
                      ) : (
                        <span className="text-xs text-agentic-text-secondary">
                          {isUsableStatus(live.status) ? 'Setting up…' : 'Paused'}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-agentic-text-secondary">
                        {minPrice === null ? (
                          ''
                        ) : minPrice.amountMinor === 0 ? (
                          <span className="text-base font-bold text-agentic-text-primary">Free</span>
                        ) : (
                          <>From <span className="text-base font-bold text-agentic-text-primary">{formatMoney(minPrice.amountMinor, minPrice.currency)}</span>/month</>
                        )}
                      </span>
                      <Button tone="custom"
                        onClick={() => navigate(`/pods/${pod.id}`)}
                        className="rounded-xl border border-agentic-gray-300 bg-white px-5 py-2 text-sm font-semibold text-agentic-text-primary hover:border-gray-400 hover:shadow-sm transition-all"
                      >
                        {sub?.status === 'canceled' ? 'Subscribe again' : 'View Pod'}
                      </Button>
                    </>
                  )}
                </div>
              </Surface>
            );
          })}
        </div>
      )}
    </>
  );
};

export default AiPodMarketplaceGrid;
